import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtEur, fmtMwhc, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronDown, Download, FileSpreadsheet, Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AdminStats } from "@/components/admin/AdminStats";

const StatusBadge = ({ status, id }) => status === "brouillon"
  ? <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700" data-testid={`admin-project-status-${id}`}>Brouillon</span>
  : <span className="rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[11px] font-semibold text-green-700" data-testid={`admin-project-status-${id}`}>Finalisé</span>;

const exportXlsx = async (partnerId) => {
  try {
    const r = await api.get("/admin/projects/export", { params: partnerId ? { partner_id: partnerId } : {}, responseType: "blob" });
    const name = /filename="([^"]+)"/.exec(r.headers["content-disposition"] || "")?.[1] || "projets.xlsx";
    const url = URL.createObjectURL(r.data);
    const a = Object.assign(document.createElement("a"), { href: url, download: name });
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export Excel téléchargé");
  } catch {
    toast.error("Échec de l'export Excel");
  }
};

const totals = (list) => list.reduce((a, p) => ({ kwhc: a.kwhc + (p.total_kwhc || 0), prime: a.prime + (p.total_prime || 0) }), { kwhc: 0, prime: 0 });

const ProjectsTable = ({ projects }) => {
  const navigate = useNavigate();
  return (
    <table className="w-full text-sm table-fixed min-w-[760px]">
      <thead>
        <tr className="bg-secondary/60 text-left">
          <th className="px-4 py-2.5 font-semibold w-[24%]">Projet</th>
          <th className="px-4 py-2.5 font-semibold w-[24%]">Utilisateur</th>
          <th className="px-4 py-2.5 font-semibold w-[12%]">Statut</th>
          <th className="px-4 py-2.5 font-semibold w-[14%]">Mis à jour</th>
          <th className="px-4 py-2.5 font-semibold w-[13%] text-right">Volume</th>
          <th className="px-4 py-2.5 font-semibold w-[13%] text-right">Prime</th>
        </tr>
      </thead>
      <tbody>
        {projects.map((p) => (
          <tr key={p.id} onClick={() => navigate(`/projects/${p.id}`)} className="border-t border-border cursor-pointer hover:bg-secondary/40 transition-colors" data-testid={`admin-project-row-${p.id}`}>
            <td className="px-4 py-3 truncate"><div className="font-medium truncate">{p.name}</div><div className="text-xs text-muted-foreground">{p.client || "—"} · zone {p.zone}</div></td>
            <td className="px-4 py-3"><div className="truncate">{p.user_name}</div><div className="text-xs text-muted-foreground truncate">{p.user_email}</div></td>
            <td className="px-4 py-3"><StatusBadge status={p.status} id={p.id} /></td>
            <td className="px-4 py-3 text-muted-foreground">{fmtDate(p.updated_at)}</td>
            <td className="px-4 py-3 text-right metric">{fmtMwhc(p.total_kwhc)}</td>
            <td className="px-4 py-3 text-right metric font-semibold">{fmtEur(p.total_prime)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const PartnerGroup = ({ group, open, onToggle }) => {
  const t = totals(group.projects);
  return (
    <Card className="overflow-hidden" data-testid={`admin-group-${group.id}`}>
      <div className="flex items-center">
      <button onClick={onToggle} className="flex-1 min-w-0 flex flex-wrap items-center gap-x-6 gap-y-2 p-4 text-left hover:bg-secondary/40 transition-colors" data-testid={`admin-group-toggle-${group.id}`}>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "" : "-rotate-90"}`} />
        <span className="font-heading font-bold text-base flex-1 min-w-[160px]">{group.name}</span>
        <span className="text-sm text-muted-foreground" data-testid={`admin-group-count-${group.id}`}><span className="metric font-semibold text-foreground">{group.projects.length}</span> projet(s)</span>
        <span className="metric text-sm" data-testid={`admin-group-kwhc-${group.id}`}>{fmtMwhc(t.kwhc)}</span>
        <span className="metric text-sm font-semibold text-genis min-w-[110px] text-right" data-testid={`admin-group-prime-${group.id}`}>{fmtEur(t.prime)}</span>
      </button>
      <button onClick={() => exportXlsx(group.id)} title={`Exporter ${group.name} (Excel)`} aria-label={`Exporter ${group.name} en Excel`} className="mr-3 h-9 w-9 shrink-0 grid place-items-center rounded-lg border border-border text-muted-foreground hover:text-genis hover:border-genis/50 transition-colors" data-testid={`admin-group-export-${group.id}`}>
        <FileSpreadsheet className="h-4 w-4" />
      </button>
      </div>
      {open && (group.projects.length
        ? <div className="border-t border-border overflow-x-auto"><ProjectsTable projects={group.projects} /></div>
        : <div className="border-t border-border py-6 text-center text-sm text-muted-foreground">Aucun projet pour ce filtre.</div>)}
    </Card>
  );
};

const Filters = ({ q, setQ, status, setStatus }) => (
  <div className="flex flex-col sm:flex-row gap-3">
    <div className="relative flex-1">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un projet, un client ou un utilisateur…" className="pl-9" data-testid="admin-projects-search" />
    </div>
    <Select value={status} onValueChange={setStatus}>
      <SelectTrigger className="sm:w-48" data-testid="admin-projects-status-filter"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Tous les statuts</SelectItem>
        <SelectItem value="brouillon">Brouillons</SelectItem>
        <SelectItem value="finalise">Finalisés</SelectItem>
      </SelectContent>
    </Select>
  </div>
);

export const AdminProjects = ({ partners, focus }) => {
  const [projects, setProjects] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [closed, setClosed] = useState({});

  useEffect(() => {
    api.get("/admin/projects").then((r) => setProjects(r.data));
  }, []);

  useEffect(() => {
    if (!focus || !projects || !partners.length) return;
    setClosed(Object.fromEntries(["genis", ...partners.map((p) => p.id)].map((id) => [id, id !== focus])));
    setTimeout(() => document.querySelector(`[data-testid="admin-group-${focus}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
  }, [focus, projects, partners]);

  const groups = useMemo(() => {
    if (!projects) return [];
    const needle = q.trim().toLowerCase();
    const filtered = projects.filter((p) =>
      (status === "all" || (status === "brouillon") === (p.status === "brouillon")) &&
      (!needle || [p.name, p.client, p.user_name, p.user_email].some((v) => (v || "").toLowerCase().includes(needle))));
    return [{ id: "genis", name: "Genis Energie" }, ...partners].map((g) => ({ ...g, projects: filtered.filter((p) => p.partner_id === g.id) }));
  }, [projects, partners, q, status]);

  return (
    <div className="space-y-5">
      <AdminStats />
      <div className="space-y-4" data-testid="admin-projects">
        <div className="flex items-center justify-between gap-3">
          <div className="font-heading font-bold text-lg">Projets par partenaire</div>
          <Button onClick={() => exportXlsx()} className="bg-genis hover:bg-genis-dark text-white" data-testid="admin-projects-export-btn">
            <Download className="h-4 w-4 mr-1.5" /> Exporter Excel
          </Button>
        </div>
        <Filters q={q} setQ={setQ} status={status} setStatus={setStatus} />
        {!projects
          ? <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-genis" /></div>
          : groups.map((g) => (
            <PartnerGroup key={g.id} group={g} open={!closed[g.id]} onToggle={() => setClosed((c) => ({ ...c, [g.id]: !c[g.id] }))} />
          ))}
      </div>
    </div>
  );
};
